import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { FAQS } from '../data/faqs'
import { ALL_GUIDES } from '../../shared/guides/index.js'
import { LIVE_TOOLS } from '../../shared/tools/index.js'

const SITE_NAME = 'PostPaid'
const SITE_URL = 'https://postpaidonline.com'
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`

const PUBLIC_ROBOTS = 'index,follow'
const PRIVATE_ROBOTS = 'noindex,follow'

const routeMeta = [
    {
        path: '/',
        title: 'PostPaid | Pay Creators for Verified Engagement',
        description: 'Launch creator campaigns where brands pay only for verified likes, comments, views, and shares. Escrow budgets, direct verification, no flat fees.',
        canonical: `${SITE_URL}/`,
        type: 'website',
        schema: ['organization', 'website', 'software'],
    },
    {
        path: '/brands',
        title: 'For Brands | Pay Per Verified Creator Interaction',
        description: 'Fund creator campaigns in escrow, approve creators, and pay only for verified engagement from Instagram and TikTok.',
        canonical: `${SITE_URL}/brands`,
        type: 'website',
        schema: ['breadcrumb'],
    },
    {
        path: '/creators',
        title: 'For Creators | Earn Per Verified Engagement',
        description: 'Join PostPaid to earn from Instagram and TikTok campaigns with no follower minimum. Get paid for verified likes, comments, shares, and views.',
        canonical: `${SITE_URL}/creators`,
        type: 'website',
        schema: ['breadcrumb'],
    },
    {
        path: '/about',
        title: 'About PostPaid | Pay-Per-Engagement Marketing',
        description: 'Learn how PostPaid replaces flat influencer fees with escrow-backed, verified, pay-per-engagement creator campaigns.',
        canonical: `${SITE_URL}/about`,
        type: 'article',
        schema: ['breadcrumb'],
    },
    {
        path: '/contact',
        title: 'Contact PostPaid | Creator Campaign Support',
        description: 'Contact the PostPaid team for brand campaigns, creator support, partnerships, security reports, or product questions.',
        canonical: `${SITE_URL}/contact`,
        type: 'contactpage',
        schema: ['breadcrumb', 'contact'],
    },
    {
        path: '/help',
        title: 'PostPaid Help Center | Creator Campaign FAQs',
        description: 'Answers about PostPaid pricing, creator eligibility, verified engagement, payouts, campaign setup, and supported platforms.',
        canonical: `${SITE_URL}/help`,
        type: 'website',
        schema: ['breadcrumb', 'faq'],
    },
    {
        path: '/tools',
        title: 'Free Influencer Marketing Tools | PostPaid',
        description: 'Free calculators and generators for engagement rates, creator earnings, campaign budgets, disclosures, and creator rate cards.',
        canonical: `${SITE_URL}/tools`,
        type: 'website',
        schema: ['breadcrumb'],
    },
    {
        path: '/privacy',
        title: 'Privacy Policy | PostPaid',
        description: 'Read how PostPaid collects, uses, shares, protects, and retains personal information for brands and creators.',
        canonical: `${SITE_URL}/privacy`,
        type: 'website',
        schema: ['breadcrumb'],
    },
]

const guideMap = Object.fromEntries(ALL_GUIDES.map((guide) => ([
    `/guides/${guide.slug}`,
    {
        title: `${guide.metaTitle}`,
        description: guide.metaDescription,
        canonical: `${SITE_URL}/guides/${guide.slug}`,
        type: 'article',
        schema: ['breadcrumb', 'article'],
        guide,
    },
])))

const toolMap = Object.fromEntries(LIVE_TOOLS.map((tool) => ([
    `/tools/${tool.slug}`,
    {
        title: tool.metaTitle,
        description: tool.metaDescription,
        canonical: `${SITE_URL}/tools/${tool.slug}`,
        type: 'website',
        schema: ['breadcrumb'],
    },
])))

function setTag(selector, attributes) {
    let el = document.head.querySelector(selector)
    if (!el) {
        el = document.createElement('meta')
        document.head.appendChild(el)
    }
    Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value))
    return el
}

function setLinkRel(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`)
    if (!el) {
        el = document.createElement('link')
        el.setAttribute('rel', rel)
        document.head.appendChild(el)
    }
    el.setAttribute('href', href)
}

function setJsonLd(id, data) {
    let el = document.getElementById(id)
    if (!data) {
        if (el) el.remove()
        return
    }
    if (!el) {
        el = document.createElement('script')
        el.type = 'application/ld+json'
        el.id = id
        document.head.appendChild(el)
    }
    el.textContent = JSON.stringify(data)
}

export function applyRouteSeo({
    title = SITE_NAME,
    description = 'PostPaid creator marketplace.',
    canonicalUrl = SITE_URL,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = 'website',
    robots = PUBLIC_ROBOTS,
    jsonLd = null,
}) {
    document.title = title
    setTag('meta[name="description"]', { name: 'description', content: description })
    setLinkRel('canonical', canonicalUrl)
    setTag('meta[name="robots"]', { name: 'robots', content: robots })
    setTag('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME })
    setTag('meta[property="og:title"]', { property: 'og:title', content: title })
    setTag('meta[property="og:description"]', { property: 'og:description', content: description })
    setTag('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl })
    setTag('meta[property="og:type"]', { property: 'og:type', content: ogType })
    setTag('meta[property="og:image"]', { property: 'og:image', content: ogImage })
    setTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
    setTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
    setTag('meta[name="twitter:description"]', { name: 'twitter:description', content: description })
    setTag('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage })
    setJsonLd('postpaid-seo-jsonld', jsonLd)
}

function buildSchema(path, config) {
    const baseOrg = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
        logo: `${SITE_URL}/favicon.svg`,
        description: 'Pay-per-engagement creator marketplace for brands and micro-creators.',
        email: 'hello@postpaidonline.com',
        sameAs: [
            'https://www.instagram.com/postpaidonline/',
            'https://www.tiktok.com/@postpaidonline',
            'https://www.linkedin.com/company/postpaidonline/',
        ],
    }

    const schemas = []
    if (config?.schema?.includes('organization')) schemas.push(baseOrg)
    if (config?.schema?.includes('website')) {
        schemas.push({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: SITE_NAME,
            url: SITE_URL,
            publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
        })
    }
    if (config?.schema?.includes('software')) {
        schemas.push({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: SITE_NAME,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            url: SITE_URL,
            description: 'Pay-per-engagement creator marketplace for verified likes, comments, shares, and views.',
        })
    }
    if (config?.schema?.includes('breadcrumb')) {
        const crumbs = [{ name: 'Home', url: SITE_URL }]
        if (path.startsWith('/guides/')) crumbs.push({ name: 'Guides', url: `${SITE_URL}/guides` })
        else if (path.startsWith('/tools/')) crumbs.push({ name: 'Tools', url: `${SITE_URL}/tools` })
        else crumbs.push({ name: path.slice(1).replace(/^\w/, (m) => m.toUpperCase()) || 'Home', url: `${SITE_URL}${path}` })
        schemas.push({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: crumbs.map((c, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                name: c.name,
                item: c.url,
            })),
        })
    }
    if (config?.schema?.includes('contact')) {
        schemas.push({
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            name: 'Contact PostPaid',
            url: `${SITE_URL}/contact`,
        })
    }
    if (config?.schema?.includes('faq')) {
        schemas.push({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
        })
    }
    if (config?.schema?.includes('article') && config.guide) {
        schemas.push({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: config.guide.title,
            description: config.guide.metaDescription,
            datePublished: config.guide.datePublished,
            dateModified: config.guide.dateModified,
            author: { '@type': 'Person', name: config.guide.author.name, url: `${SITE_URL}${config.guide.author.url}` },
            mainEntityOfPage: `${SITE_URL}${path}`,
            publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
        })
    }
    return schemas.length === 1 ? schemas[0] : schemas
}

export default function RouteSeo() {
    const { pathname } = useLocation()

    useEffect(() => {
        const config = routeMeta.find((entry) => entry.path === pathname) || guideMap[pathname] || toolMap[pathname]
        const isPrivate = pathname.startsWith('/dashboard') || pathname.startsWith('/admin') || pathname.startsWith('/login') || pathname.startsWith('/register') || pathname.startsWith('/forgot-password') || pathname.startsWith('/reset-password') || pathname.startsWith('/verify-email')

        applyRouteSeo({
            title: config?.title,
            description: config?.description,
            canonicalUrl: config?.canonical || `${SITE_URL}${pathname}`,
            ogType: config?.type,
            robots: isPrivate ? PRIVATE_ROBOTS : PUBLIC_ROBOTS,
            jsonLd: buildSchema(pathname, config),
        })
    }, [pathname])

    return null
}
